/**
 * Screen Shake System
 *
 * Adds camera shake effects for impacts, explosions, etc.
 */

export interface ShakeConfig {
  intensity: number;    // Max pixel displacement
  duration: number;     // Seconds
  frequency?: number;   // Shake speed (default 30)
  decay?: boolean;      // Whether to fade out (default true)
}

interface ActiveShake {
  intensity: number;
  duration: number;
  elapsed: number;
  frequency: number;
  decay: boolean;
}

export class ScreenShake {
  private shakes: ActiveShake[] = [];
  private offsetX: number = 0;
  private offsetY: number = 0;

  // Hitstop (freeze frame) system
  private hitStopTimer: number = 0;

  // Screen flash system
  private flashColor: string = '';
  private flashAlpha: number = 0;
  private flashTimer: number = 0;
  private flashDuration: number = 0;

  /**
   * Add a new screen shake
   */
  shake(config: ShakeConfig): void {
    this.shakes.push({
      intensity: config.intensity,
      duration: config.duration,
      elapsed: 0,
      frequency: config.frequency || 30,
      decay: config.decay !== false,
    });
  }

  /**
   * Preset: Small hit shake
   */
  shakeHit(): void {
    this.shake({
      intensity: 3,
      duration: 0.1,
      frequency: 40,
    });
  }

  /**
   * Preset: Heavy hit shake
   */
  shakeHeavyHit(): void {
    this.shake({
      intensity: 8,
      duration: 0.2,
      frequency: 35,
    });
  }

  /**
   * Preset: Explosion shake
   */
  shakeExplosion(): void {
    this.shake({
      intensity: 15,
      duration: 0.4,
      frequency: 25,
    });
  }

  /**
   * Preset: Boss ability shake
   */
  shakeBossAbility(): void {
    this.shake({
      intensity: 12,
      duration: 0.5,
      frequency: 20,
    });
  }

  /**
   * Preset: Level up celebration shake
   */
  shakeLevelUp(): void {
    this.shake({
      intensity: 5,
      duration: 0.3,
      frequency: 50,
    });
  }

  /**
   * Preset: Death shake
   */
  shakeDeath(): void {
    this.shake({
      intensity: 20,
      duration: 0.6,
      frequency: 15,
    });
  }

  /**
   * Trigger a hitstop (brief time freeze) for impact feel
   */
  triggerHitStop(duration: number = 0.04): void {
    this.hitStopTimer = duration;
  }

  /**
   * Check if game should be paused for hitstop
   */
  isHitStopped(): boolean {
    return this.hitStopTimer > 0;
  }

  /**
   * Tick down the hit-stop timer (called from GameLoop when game update is frozen)
   */
  tickHitStop(deltaTime: number): void {
    if (this.hitStopTimer > 0) {
      this.hitStopTimer = Math.max(0, this.hitStopTimer - deltaTime);
    }
  }

  /**
   * Trigger a screen flash overlay
   */
  triggerFlash(color: string = '#ffffff', duration: number = 0.1, alpha: number = 0.3): void {
    this.flashColor = color;
    this.flashAlpha = alpha;
    this.flashTimer = duration;
    this.flashDuration = duration;
  }

  /**
   * Get flash rendering state
   */
  getFlash(): { color: string; alpha: number } | null {
    if (this.flashTimer <= 0) return null;
    const progress = this.flashTimer / this.flashDuration;
    return { color: this.flashColor, alpha: this.flashAlpha * progress };
  }

  /**
   * Update all active shakes
   */
  update(deltaTime: number): void {
    // Update hitstop timer
    if (this.hitStopTimer > 0) {
      this.hitStopTimer = Math.max(0, this.hitStopTimer - deltaTime);
    }

    // Update flash timer
    if (this.flashTimer > 0) {
      this.flashTimer = Math.max(0, this.flashTimer - deltaTime);
    }

    this.offsetX = 0;
    this.offsetY = 0;

    // Process each shake and combine their offsets
    for (let i = this.shakes.length - 1; i >= 0; i--) {
      const shake = this.shakes[i];

      shake.elapsed += deltaTime;

      if (shake.elapsed >= shake.duration) {
        this.shakes[i] = this.shakes[this.shakes.length - 1];
        this.shakes.pop();
        continue;
      }

      // Calculate current intensity (with optional decay)
      let currentIntensity = shake.intensity;
      if (shake.decay) {
        const progress = shake.elapsed / shake.duration;
        currentIntensity = shake.intensity * (1 - progress);
      }

      // Generate shake offset using Perlin-like noise pattern
      const time = shake.elapsed * shake.frequency;
      const noise1 = Math.sin(time) * Math.cos(time * 0.7);
      const noise2 = Math.cos(time * 1.3) * Math.sin(time * 0.5);

      this.offsetX += noise1 * currentIntensity;
      this.offsetY += noise2 * currentIntensity;
    }
  }

  /**
   * Get current X offset to apply to camera
   */
  getOffsetX(): number {
    return this.offsetX;
  }

  /**
   * Get current Y offset to apply to camera
   */
  getOffsetY(): number {
    return this.offsetY;
  }

  /**
   * Check if any shakes are active
   */
  isShaking(): boolean {
    return this.shakes.length > 0;
  }

  /**
   * Stop all shakes immediately
   */
  stopAll(): void {
    this.shakes = [];
    this.offsetX = 0;
    this.offsetY = 0;
  }
}

// Singleton instance
let screenShakeInstance: ScreenShake | null = null;

export function getScreenShake(): ScreenShake {
  if (!screenShakeInstance) {
    screenShakeInstance = new ScreenShake();
  }
  return screenShakeInstance;
}
